import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Calendar, CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { DeleteRecordTypeModal } from "@/components/Organisms/DeleteRecordTypeModal/DeleteRecordTypeModal";
import type { DrawerHeaderMetaItem } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import { EMPTY_ORG_ID } from "@/constants/org";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import { EntityTypeDrawer } from "@/features/entity-types/components/entity-type-drawer";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { useSyncedLocationRequired } from "@/features/entity-types/hooks/use-synced-location-required";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import {
	useDuplicateRecordType,
	useUpdateRecordType,
} from "@/services/record/record.mutations";
import { recordQueryOptions } from "@/services/record/record.options";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";
import {
	canShowActivityTab,
	getRecordTypeDisplayName,
} from "@/utils/record-type-detail/record-type-detail.utils";
import {
	RecordTypeDrawerContext,
	type RecordTypeDrawerContextValue,
} from "@/utils/record-type-detail/record-type-drawer-context";
import { showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z
		.enum(["drawer", "modal", "full"])
		.optional()
		.default("drawer"),
});

type RecordTypeTab = "details" | "schema" | "rules" | "activity";

function getCurrentTab(pathname: string): RecordTypeTab {
	const segment = pathname.split("/").pop();
	if (
		segment === "details" ||
		segment === "schema" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/configuration/recordTypes/$recordTypeId",
)({
	component: AdminRecordTypeIdLayout,
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params, search }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/recordTypes/${params.recordTypeId}`,
		);
		if (exactRoot) {
			const parsed = SearchSchema.safeParse(search);
			const s = parsed.success ? parsed.data : SearchSchema.parse({});
			throw redirect({
				to: "/admin/configuration/recordTypes/$recordTypeId/details",
				params: { recordTypeId: params.recordTypeId },
				search: { mode: s.mode, presentation: s.presentation },
			});
		}
	},
});

function AdminRecordTypeIdLayout() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/recordTypes/$recordTypeId",
	});

	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/recordTypes/$recordTypeId",
	});

	const isFullRoute = /\/configuration\/recordTypes\/[^/]+\/full/.test(
		pathname,
	);
	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const detailQuery = useQuery(
		recordQueryOptions.recordTypeDetail(params.recordTypeId, {
			organizationId: EMPTY_ORG_ID,
		}),
	);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteRecordType",
			entityType: "RecordType",
			entityId: params.recordTypeId,
		}),
	});

	const recordsCountQuery = useQuery({
		...recordQueryOptions.recordsCount({
			RecordTypeIds: [params.recordTypeId],
		}),
		enabled: !!canDeleteQuery.data,
	});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditRecordType",
			entityType: "RecordType",
			entityId: params.recordTypeId,
		}),
	});
	const updateMutation = useUpdateRecordType();
	const duplicateMutation = useDuplicateRecordType();
	const { duplicate: duplicateRecordType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (recordTypeId) => ({
				recordTypeId,
				organizationId: EMPTY_ORG_ID,
			}),
			onDuplicated: (recordTypeId) => {
				void navigate({
					to: "/admin/configuration/recordTypes/$recordTypeId/details",
					params: { recordTypeId },
					search: { mode: "view", presentation: "drawer" },
				});
			},
			errorMessage: t("admin.recordTypes.modal.createError"),
		});

	const recordType = detailQuery.data ?? null;
	const [locationRequired, setLocationRequired] = useSyncedLocationRequired(
		recordType?.id,
		recordType?.locationRequired,
	);

	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [editedIcon, setEditedIcon] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftIcon, setDraftIcon] = useState("");

	useEffect(() => {
		if (!recordType) return;
		const name = getRecordTypeDisplayName(recordType);
		const description = recordType.description ?? "";
		const icon = recordType.icon ?? "";
		if (search.mode !== "edit" || draftName === editedName) {
			setEditedName(name);
			setDraftName(name);
		}
		if (search.mode !== "edit" || draftDescription === editedDescription) {
			setEditedDescription(description);
			setDraftDescription(description);
		}
		if (search.mode !== "edit" || draftIcon === editedIcon) {
			setEditedIcon(icon);
			setDraftIcon(icon);
		}
	}, [recordType]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/admin/configuration/recordTypes",
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) handleClose();
	};

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || recordType === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && recordType !== null;

	const loadedRecordType = recordType ?? undefined;

	const displayRecordType =
		loadedRecordType && isDetailReady
			? {
					...loadedRecordType,
					name: editedName,
					description: editedDescription,
					icon: editedIcon || loadedRecordType.icon,
				}
			: null;

	const canEdit = !!loadedRecordType && isDetailReady && !!canEditQuery.data;
	const showActivity =
		!!loadedRecordType &&
		isDetailReady &&
		canShowActivityTab("platform", loadedRecordType);
	const currentTab = getCurrentTab(pathname);
	const isEditing = search.mode === "edit";

	const isValid = draftName.trim().length > 0;

	const tabs: { id: RecordTypeTab; label: string }[] = isDetailReady
		? [
				{ id: "details", label: t("admin.recordTypes.drawer.tabs.details") },
				{ id: "schema", label: t("admin.recordTypes.drawer.tabs.form") },
				{ id: "rules", label: t("admin.recordTypes.drawer.tabs.rules") },
				...(showActivity
					? [
							{
								id: "activity" as const,
								label: t("admin.recordTypes.drawer.tabs.activity"),
							},
						]
					: []),
			]
		: [];

	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ id: currentTabInfo.id, label: `Edit ${currentTabInfo.label}` }]
		: [];

	const canDelete =
		!!loadedRecordType &&
		isDetailReady &&
		!!canDeleteQuery.data &&
		!isEditing &&
		recordsCountQuery.data === 0;
	const canDuplicate = isDetailReady && !isEditing;

	const saveDraftFn = async (): Promise<void> => {
		if (!loadedRecordType || !isValid) return;
		try {
			await updateMutation.mutateAsync({
				id: loadedRecordType.id,
				data: {
					name: draftName.trim(),
					description: draftDescription || null,
					icon: draftIcon || null,
					subOrganizationIds: null,
					locationRequired,
				},
				organizationId: EMPTY_ORG_ID,
			});
			setEditedName(draftName.trim());
			setEditedDescription(draftDescription);
			setEditedIcon(draftIcon);
			showSuccessToast({
				message: t("admin.recordTypes.drawer.details.toast.saveSuccess"),
			});
			void navigate({
				to: `/admin/configuration/recordTypes/${params.recordTypeId}/details`,
				search: { mode: "view" },
			});
		} catch {
			// Global mutation error handler already shows the error toast
		}
	};

	const persistDetails = async (
		patch: EntityTypeDetailsPatch,
	): Promise<void> => {
		if (!loadedRecordType) return;
		const name = (patch.name ?? editedName).trim();
		const description =
			patch.description !== undefined
				? (patch.description ?? "")
				: editedDescription;
		const icon = patch.icon !== undefined ? (patch.icon ?? "") : editedIcon;
		await updateMutation.mutateAsync({
			id: loadedRecordType.id,
			data: {
				name,
				description: description || null,
				icon: icon || null,
				subOrganizationIds: null,
				locationRequired,
			},
			organizationId: EMPTY_ORG_ID,
		});
		setEditedName(name);
		setDraftName(name);
		setEditedDescription(description);
		setDraftDescription(description);
		setEditedIcon(icon);
		setDraftIcon(icon);
	};

	const persistLocationRequired = async (next: boolean): Promise<void> => {
		if (!loadedRecordType) return;
		await updateMutation.mutateAsync({
			id: loadedRecordType.id,
			data: {
				name: (editedName || loadedRecordType.name || "").trim(),
				description: editedDescription || loadedRecordType.description || null,
				icon: editedIcon || loadedRecordType.icon || null,
				subOrganizationIds: null,
				locationRequired: next,
			},
			organizationId: EMPTY_ORG_ID,
		});
		setLocationRequired(next);
	};

	const contextValue: RecordTypeDrawerContextValue | null =
		loadedRecordType && isDetailReady && displayRecordType
			? {
					scope: "platform",
					recordType: displayRecordType,
					mode: search.mode,
					setMode: (mode) => {
						void navigate({
							to: `/admin/configuration/recordTypes/${params.recordTypeId}/details`,
							search: { mode },
						});
					},
					activeTab: currentTab,
					canEdit,
					draftName,
					draftDescription,
					draftIcon,
					setDraftName,
					setDraftDescription,
					setDraftIcon,
					draftSubOrgIds: null,
					setDraftSubOrgIds: () => {},
					isSaving: updateMutation.isPending,
					isValid,
					isDirty:
						draftName.trim() !== editedName ||
						draftDescription !== editedDescription ||
						draftIcon !== editedIcon,
					saveDraft: saveDraftFn,
					cancelDraft: () => {
						setDraftName(editedName);
						setDraftDescription(editedDescription);
						setDraftIcon(editedIcon);
						void navigate({
							to: `/admin/configuration/recordTypes/${params.recordTypeId}/details`,
							search: { mode: "view" },
						});
					},
					persistDetails,
					locationRequired,
					persistLocationRequired,
				}
			: null;

	const headerTitle =
		displayRecordType !== null
			? getRecordTypeDisplayName(displayRecordType)
			: t("admin.recordTypes.drawer.header.titlePlaceholder");

	const drawerMetadata: DrawerHeaderMetaItem[] = [];
	if (loadedRecordType?.createdAt) {
		drawerMetadata.push({
			id: "created",
			label: createdMetadataLabel(t, loadedRecordType.createdAt),
			icon: Calendar,
		});
	}
	if (loadedRecordType?.updatedAt) {
		drawerMetadata.push({
			id: "updated",
			label: updatedMetadataLabel(t, loadedRecordType.updatedAt),
			icon: CalendarCheck01,
		});
	}

	if (isFullRoute) {
		return contextValue ? (
			<RecordTypeDrawerContext.Provider value={contextValue}>
				<Outlet />
			</RecordTypeDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	const isOnRecordTypeSubroute = /\/configuration\/recordTypes\/[^/]+\//.test(
		pathname,
	);

	if (!isOnRecordTypeSubroute) {
		return null;
	}

	return (
		<>
			<EntityTypeDrawer
				type="record"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				title={headerTitle}
				metadata={drawerMetadata}
				workspaceLabel={t("main.jobs.workspace.action")}
				showWorkspace={isDetailReady}
				onWorkspace={() => {
					void navigate({
						to: "/admin/configuration/recordTypes/$recordTypeId/full",
						params: { recordTypeId: params.recordTypeId },
						search: {
							tab: "details",
							mode: "view",
							presentation: "full",
						},
					});
				}}
				duplicateLabel={
					canDuplicate
						? t("admin.recordTypes.table.actions.duplicate")
						: undefined
				}
				onDuplicate={
					canDuplicate
						? () => duplicateRecordType(params.recordTypeId)
						: undefined
				}
				deleteLabel={
					canDelete
						? t("admin.recordTypes.drawer.footer.deleteRecordType")
						: undefined
				}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				isLoading={isDetailLoading}
				isError={isDetailError}
				errorTitle={t("admin.recordTypes.drawer.errors.loadFailed")}
				errorDescription={t("admin.recordTypes.drawer.errors.notFound")}
				tabs={isEditing ? editModeTabs : tabs}
				currentTab={currentTab}
				onTabChange={(tabId) =>
					void navigate({
						to: `/admin/configuration/recordTypes/${params.recordTypeId}/${tabId}`,
						search: { mode: search.mode },
					})
				}
				data-testid="record-type-drawer"
			>
				{isDetailReady && contextValue ? (
					<RecordTypeDrawerContext.Provider value={contextValue}>
						<Outlet />
					</RecordTypeDrawerContext.Provider>
				) : null}
			</EntityTypeDrawer>

			<DeleteRecordTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={handleClose}
				recordTypeId={params.recordTypeId}
				recordTypeName={
					loadedRecordType ? getRecordTypeDisplayName(loadedRecordType) : ""
				}
				organizationId={EMPTY_ORG_ID}
			/>

			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.recordTypes.duplicate.inProgress")}
				testId="recordtype-drawer-duplicate-in-progress"
			/>
		</>
	);
}
